// client/src/components/secure/Showings.js
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

const STATUSES = ['Requested', 'Confirmed', 'Declined', 'Completed', 'Cancelled', 'No Show'];

const statusStyle = (status) => {
    if (status === 'Completed') return 'bg-green-100 text-green-800';
    if (status === 'Cancelled' || status === 'No Show') return 'bg-gray-100 text-gray-600';
    if (status === 'Confirmed') return 'bg-blue-100 text-blue-800';
    return 'bg-yellow-100 text-yellow-800';
};

const nowLocalDatetime = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
};

const ShowingRow = ({ showing, onUpdated }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [status, setStatus] = useState(showing.status);
    const [feedback, setFeedback] = useState(showing.feedback || '');
    const [isSaving, setIsSaving] = useState(false);
    const apiClient = useApiClient();

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await apiClient.put(`/api/showings/${showing.showing_id}`, {
                client_id: showing.client_id,
                agent_id: showing.agent_id,
                scheduled_start: showing.scheduled_start,
                scheduled_end: showing.scheduled_end,
                status,
                feedback,
                notes: showing.notes,
            });
            setIsEditing(false);
            onUpdated();
        } catch (err) {
            alert(err.message || 'Failed to update showing.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <li className="py-3">
            <div className="flex justify-between items-baseline">
                <div>
                    <span className="font-semibold text-slate-800">{new Date(showing.scheduled_start).toLocaleString()}</span>
                    {showing.client_name && <span className="text-slate-600"> — {showing.client_name}</span>}
                    {showing.agent_name && <span className="text-slate-400 text-sm"> (Agent: {showing.agent_name})</span>}
                </div>
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyle(showing.status)}`}>{showing.status}</span>
            </div>
            {showing.notes && <p className="text-slate-600 text-sm mt-1">{showing.notes}</p>}
            {showing.feedback && !isEditing && <p className="text-slate-500 text-sm italic mt-1">Feedback: {showing.feedback}</p>}

            {!isEditing ? (
                <button onClick={() => setIsEditing(true)} className="text-sm text-indigo-600 hover:underline mt-1">Update</button>
            ) : (
                <div className="mt-2 bg-slate-50 p-3 rounded space-y-2">
                    <select value={status} onChange={e => setStatus(e.target.value)} className="p-2 border rounded w-full">
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <textarea value={feedback} onChange={e => setFeedback(e.target.value)} placeholder="Feedback (optional)" rows="2" className="p-2 border rounded w-full"></textarea>
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setIsEditing(false)} className="px-3 py-1 text-sm bg-gray-200 rounded">Cancel</button>
                        <button onClick={handleSave} disabled={isSaving} className="px-3 py-1 text-sm bg-slate-800 text-white rounded disabled:bg-indigo-400">{isSaving ? 'Saving...' : 'Save'}</button>
                    </div>
                </div>
            )}
        </li>
    );
};

const Showings = ({ propertyId }) => {
    const [showings, setShowings] = useState([]);
    const [clients, setClients] = useState([]);
    const [agents, setAgents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formData, setFormData] = useState({ client_id: '', agent_id: '', scheduled_start: '', notes: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const apiClient = useApiClient();

    const fetchShowings = useCallback(() => {
        setIsLoading(true);
        apiClient.get(`/api/showings?property_id=${propertyId}`)
            .then(setShowings)
            .catch(err => setError(err.message || 'Failed to load showings.'))
            .finally(() => setIsLoading(false));
    }, [propertyId, apiClient]);

    useEffect(() => {
        fetchShowings();
    }, [fetchShowings]);

    const openForm = () => {
        apiClient.get('/api/clients').then(setClients).catch(() => setClients([]));
        apiClient.get('/api/agents').then(setAgents).catch(() => setAgents([]));
        setFormData({ client_id: '', agent_id: '', scheduled_start: nowLocalDatetime(), notes: '' });
        setIsFormOpen(true);
    };

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        try {
            await apiClient.post('/api/showings', { ...formData, property_id: propertyId });
            setIsFormOpen(false);
            fetchShowings();
        } catch (err) {
            setError(err.message || 'Failed to schedule showing.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white p-8 rounded-lg shadow mt-8">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-slate-800">Showings</h2>
                <button onClick={openForm} className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900">
                    Schedule Showing
                </button>
            </div>

            {isFormOpen && (
                <form onSubmit={handleSubmit} className="bg-slate-50 p-4 rounded-lg mb-6 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <select name="client_id" value={formData.client_id} onChange={handleChange} className="p-2 border rounded">
                            <option value="">No client selected</option>
                            {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.first_name} {c.last_name}</option>)}
                        </select>
                        <select name="agent_id" value={formData.agent_id} onChange={handleChange} className="p-2 border rounded">
                            <option value="">Unassigned</option>
                            {agents.map(a => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}
                        </select>
                    </div>
                    <input type="datetime-local" name="scheduled_start" value={formData.scheduled_start} onChange={handleChange} required className="p-2 border rounded w-full" />
                    <input name="notes" value={formData.notes} onChange={handleChange} placeholder="Notes (optional)" className="p-2 border rounded w-full" />
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <div className="flex justify-end gap-3">
                        <button type="button" onClick={() => setIsFormOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            )}

            {isLoading && <p className="text-slate-500">Loading showings...</p>}
            {!isLoading && error && !isFormOpen && <p className="text-red-500">{error}</p>}
            {!isLoading && (
                <ul className="divide-y divide-gray-200">
                    {showings.map(s => <ShowingRow key={s.showing_id} showing={s} onUpdated={fetchShowings} />)}
                    {showings.length === 0 && <p className="text-slate-500 py-4">No showings scheduled yet.</p>}
                </ul>
            )}
        </div>
    );
};

export default Showings;
